import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, Collapse, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, List, ListItemButton, ListItemText, Paper,
  Skeleton, Snackbar, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import {
  BsChevronDown, BsChevronUp, BsPencil, BsPlusLg, BsTrash,
} from 'react-icons/bs';

import axiosInstance from '../../axiosInstance';
import CorrectTextModal from '../Report/CorrectTextModal';
import DetailDialog from './detailDialog';
import Fiche from '../Expert/Fiche';
import PosteDialog from './posteDialog';
import Recapitulatif from './recapitulatif';
import { euros, libelleOrigine, libelleTva, nombre } from './constantes';
import { themeExpert } from '../../theme/expert';

const POSTE_VIDE = {
  title: '', comment: '', reclamer: 0, calculer: 0, accorder: 0, recours: 0,
  tva: 17, origin: '1',
};

const LIGNE_VIDE = { title: '', unitValue: 0, unit: 'm2', quantity: 0 };

/** Les quatre totaux du dossier, hors TVA et TTC. */
const TOTAUX = [
  { htva: 'totalReclamerHtva', ttc: 'totalReclamerTtc', libelle: 'Réclamé' },
  { htva: 'totalCalculerHtva', ttc: 'totalCalculerTtc', libelle: 'Calculé' },
  { htva: 'totalAccorderHtva', ttc: 'totalAccorderTtc', libelle: 'Assuré' },
  { htva: 'totalRecoursHtva', ttc: 'totalRecoursTtc', libelle: 'Recours' },
];

function ListCosting() {
  const { expertiseId } = useParams();

  const [totaux, setTotaux] = useState(null);
  const [postes, setPostes] = useState([]);
  const [lignes, setLignes] = useState({});
  const [ouverts, setOuverts] = useState({});
  const [erreur, setErreur] = useState('');
  const [message, setMessage] = useState('');
  const [occupe, setOccupe] = useState(false);

  const [poste, setPoste] = useState(null);
  const [ligne, setLigne] = useState(null);
  const [posteDeLaLigne, setPosteDeLaLigne] = useState(null);
  const [aCorriger, setACorriger] = useState(null);
  const [posteASupprimer, setPosteASupprimer] = useState(null);
  const [ligneASupprimer, setLigneASupprimer] = useState(null);
  const [recapitulatif, setRecapitulatif] = useState(false);

  const [modeles, setModeles] = useState(null);
  const [posteCible, setPosteCible] = useState(null);

  const charger = useCallback(async () => {
    try {
      const [somme, liste] = await Promise.all([
        axiosInstance.get(`/totalcostings/${expertiseId}/`),
        axiosInstance.get('/postcostings/', { params: { expertise_id: expertiseId } }),
      ]);
      setTotaux(somme.data);
      setPostes(liste.data.results || liste.data);
      setErreur('');
    } catch {
      setErreur("Le chiffrage n'a pas pu être chargé.");
    }
  }, [expertiseId]);

  useEffect(() => { charger(); }, [charger]);

  const chargerLignes = useCallback(async (posteId) => {
    const { data } = await axiosInstance.get('/detailcostings/',
                                             { params: { postCostingId: posteId } });
    setLignes((etat) => ({ ...etat, [posteId]: data.results || data }));
  }, []);

  const basculer = async (posteId) => {
    if (ouverts[posteId]) {
      setOuverts((etat) => ({ ...etat, [posteId]: false }));
      return;
    }
    try {
      await chargerLignes(posteId);
      setOuverts((etat) => ({ ...etat, [posteId]: true }));
    } catch {
      setErreur("Les lignes de ce poste n'ont pas pu être chargées.");
    }
  };

  // Le serveur recalcule les totaux du poste à chaque écriture : on les
  // relit plutôt que de les recomposer ici, où ils finiraient par diverger.
  const rafraichir = async (posteId) => {
    await chargerLignes(posteId);
    await charger();
  };

  const enregistrerPoste = async (e) => {
    e.preventDefault();
    setOccupe(true);
    try {
      const corps = {
        ...poste,
        reclamer: nombre(poste.reclamer), calculer: nombre(poste.calculer),
        accorder: nombre(poste.accorder), recours: nombre(poste.recours),
      };
      if (poste.id) {
        await axiosInstance.put(`/postcostings/${poste.id}/`, corps);
      } else {
        await axiosInstance.post('/postcostings/',
                                 { ...corps, totalCosting: totaux.id });
      }
      setPoste(null);
      setMessage('Poste enregistré');
      charger();
    } catch {
      setErreur("Le poste n'a pas pu être enregistré.");
    } finally {
      setOccupe(false);
    }
  };

  const supprimerPoste = async () => {
    setOccupe(true);
    try {
      await axiosInstance.delete(`/postcostings/${posteASupprimer.id}/`);
      setPosteASupprimer(null);
      setMessage('Poste supprimé');
      charger();
    } catch {
      setErreur("Le poste n'a pas pu être supprimé.");
    } finally {
      setOccupe(false);
    }
  };

  const enregistrerLigne = async (valeurs) => {
    setOccupe(true);
    try {
      const corps = {
        ...valeurs,
        unitValue: nombre(valeurs.unitValue),
        quantity: nombre(valeurs.quantity),
        postCosting: posteDeLaLigne,
      };
      if (valeurs.id) {
        await axiosInstance.put(`/detailcostings/${valeurs.id}/`, corps);
      } else {
        await axiosInstance.post('/detailcostings/', corps);
      }
      setLigne(null);
      setMessage('Ligne enregistrée');
      await rafraichir(posteDeLaLigne);
    } catch {
      setErreur("La ligne n'a pas pu être enregistrée.");
    } finally {
      setOccupe(false);
    }
  };

  const supprimerLigne = async () => {
    setOccupe(true);
    const posteId = ligneASupprimer.postCosting;
    try {
      await axiosInstance.delete(`/detailcostings/${ligneASupprimer.id}/`);
      setLigneASupprimer(null);
      setMessage('Ligne supprimée');
      // L'écran ne recalculait que son propre total et gardait les montants
      // du serveur tels quels : le poste affichait un chiffrage périmé.
      await rafraichir(posteId);
    } catch {
      setErreur("La ligne n'a pas pu être supprimée.");
    } finally {
      setOccupe(false);
    }
  };

  const ouvrirModeles = async (posteId) => {
    try {
      const { data } = await axiosInstance.get('/model_pricings/');
      setModeles(data.results || data);
      setPosteCible(posteId);
    } catch {
      setErreur("Les modèles de prix n'ont pas pu être chargés.");
    }
  };

  const importerModele = async (modele) => {
    setOccupe(true);
    try {
      await axiosInstance.post(
        `/model_pricings/${modele.id}/import_to_post/${posteCible}/`);
      setModeles(null);
      setMessage(`Modèle « ${modele.title} » importé`);
      await rafraichir(posteCible);
      setOuverts((etat) => ({ ...etat, [posteCible]: true }));
    } catch {
      setErreur("Le modèle n'a pas pu être importé.");
    } finally {
      setOccupe(false);
    }
  };

  /** La fourchette du barème, quand le poste en porte une. */
  const fourchette = (poste_) => {
    const min = nombre(poste_.total_min);
    const max = nombre(poste_.total_max);
    if (!min && !max) return null;
    if (min === max) return null;
    return `${euros(min)} – ${euros(max)}`;
  };

  if (!totaux) {
    return <Stack spacing={2}>
      <Skeleton height={140} /><Skeleton height={90} /><Skeleton height={90} />
    </Stack>;
  }

  return (
    <>
      <Stack spacing={3}>
        {erreur && <Alert severity="error"
                          onClose={() => setErreur('')}>{erreur}</Alert>}

        <Fiche titre="Totaux du dossier"
               explication="Hors TVA, puis TVA comprise.">
          <Box sx={{ display: 'grid', gap: 2,
                     gridTemplateColumns: {
                       xs: '1fr 1fr', md: 'repeat(4, 1fr)' } }}>
            {TOTAUX.map(({ htva, ttc, libelle }) => (
              <Box key={htva}>
                <Typography variant="caption" color="text.secondary"
                            component="div">
                  {libelle}
                </Typography>
                <Typography variant="h2">{euros(totaux[htva])}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {euros(totaux[ttc])} TTC
                </Typography>
              </Box>
            ))}
          </Box>
        </Fiche>

        <Fiche
          titre={`Postes (${postes.length})`}
          explication="Un poste par dommage chiffré. Les lignes en dessous font son montant calculé."
          actions={(
            <Stack direction="row" spacing={1.5}>
              <Button size="small" variant="outlined"
                      onClick={() => setRecapitulatif(true)}
                      disabled={postes.length === 0}>
                Récapitulatif
              </Button>
              <Button size="small" variant="contained"
                      startIcon={<BsPlusLg />}
                      onClick={() => setPoste({ ...POSTE_VIDE })}>
                Ajouter un poste
              </Button>
            </Stack>
          )}
          sx={{ p: 0 }}
        >
          {postes.length === 0 ? (
            <Typography variant="body2" color="text.secondary"
                        sx={{ py: 4, textAlign: 'center' }}>
              Aucun poste. Commencez par en ajouter un.
            </Typography>
          ) : (
            <Stack divider={<Box sx={{ borderTop: '1px solid',
                                       borderColor: 'divider' }} />}>
              {postes.map((p) => (
                <Box key={p.id}>
                  <Box sx={{ p: 2 }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}
                           justifyContent="space-between"
                           alignItems={{ md: 'flex-start' }}>
                      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="baseline"
                               flexWrap="wrap" useFlexGap>
                          <Typography variant="h3">
                            {p.index} {p.title}
                          </Typography>
                          <Chip size="small" variant="outlined"
                                label={libelleOrigine(p.origin)} />
                          <Chip size="small" variant="outlined"
                                label={libelleTva(p.tva)} />
                        </Stack>

                        <Stack direction="row" spacing={3} flexWrap="wrap"
                               useFlexGap sx={{ mt: 1 }}>
                          {[['Réclamé', p.reclamer], ['Calculé', p.calculer],
                            ['Assuré', p.accorder], ['Recours', p.recours]]
                            .map(([libelle, valeur]) => (
                              <Box key={libelle}>
                                <Typography variant="caption"
                                            color="text.secondary"
                                            component="div">
                                  {libelle}
                                </Typography>
                                <Typography variant="body1"
                                            sx={{ fontWeight: 600 }}>
                                  {euros(valeur)}
                                </Typography>
                              </Box>
                            ))}
                          {fourchette(p) && (
                            <Box>
                              <Typography variant="caption"
                                          color="text.secondary"
                                          component="div">
                                Fourchette du barème
                              </Typography>
                              <Typography variant="body2">
                                {fourchette(p)}
                              </Typography>
                            </Box>
                          )}
                        </Stack>

                        {p.comment && (
                          <Box className="texte-riche"
                               sx={{ mt: 1.5, '& p:last-child': { mb: 0 } }}
                               dangerouslySetInnerHTML={{ __html: p.comment }} />
                        )}
                      </Box>

                      <Stack direction="row" sx={{ flexShrink: 0 }}>
                        <Tooltip title="Faire relire le titre et l'analyse">
                          <IconButton size="small"
                                      onClick={() => setACorriger(p)}>
                            ✨
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Modifier">
                          <IconButton size="small"
                                      onClick={() => setPoste({
                                        ...POSTE_VIDE, ...p })}>
                            <BsPencil />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Supprimer">
                          <IconButton size="small"
                                      onClick={() => setPosteASupprimer(p)}>
                            <BsTrash />
                          </IconButton>
                        </Tooltip>
                        <Button size="small" onClick={() => basculer(p.id)}
                                endIcon={ouverts[p.id] ? <BsChevronUp />
                                                        : <BsChevronDown />}>
                          {ouverts[p.id] ? 'Masquer' : 'Chiffrage'}
                        </Button>
                      </Stack>
                    </Stack>
                  </Box>

                  <Collapse in={Boolean(ouverts[p.id])} unmountOnExit>
                    <Box sx={{ px: 2, pb: 2 }}>
                      <Paper variant="outlined">
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Libellé</TableCell>
                                <TableCell align="right">P.U.</TableCell>
                                <TableCell align="center">Unité</TableCell>
                                <TableCell align="right">Quantité</TableCell>
                                <TableCell align="right">Total</TableCell>
                                <TableCell align="right">&nbsp;</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {(lignes[p.id] || []).length === 0 && (
                                <TableRow>
                                  <TableCell colSpan={6}
                                             sx={{ py: 3,
                                                   textAlign: 'center' }}>
                                    <Typography variant="body2"
                                                color="text.secondary">
                                      Aucune ligne pour ce poste.
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              )}
                              {(lignes[p.id] || []).map((l) => (
                                <TableRow key={l.id} hover>
                                  <TableCell>{l.title}</TableCell>
                                  <TableCell align="right">
                                    {euros(l.unitValue)}
                                  </TableCell>
                                  <TableCell align="center">{l.unit}</TableCell>
                                  <TableCell align="right">
                                    {l.quantity}
                                  </TableCell>
                                  <TableCell align="right"
                                             sx={{ fontWeight: 600 }}>
                                    {euros(l.total)}
                                  </TableCell>
                                  <TableCell align="right"
                                             sx={{ whiteSpace: 'nowrap' }}>
                                    <IconButton size="small"
                                                aria-label="Modifier"
                                                onClick={() => {
                                                  setPosteDeLaLigne(p.id);
                                                  setLigne({ ...LIGNE_VIDE,
                                                             ...l });
                                                }}>
                                      <BsPencil />
                                    </IconButton>
                                    <IconButton size="small"
                                                aria-label="Supprimer"
                                                onClick={() => setLigneASupprimer(
                                                  { ...l, postCosting: p.id })}>
                                      <BsTrash />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>

                        <Stack direction={{ xs: 'column', sm: 'row' }}
                               spacing={2} alignItems="center"
                               justifyContent="space-between"
                               sx={{ p: 2, borderTop: '1px solid',
                                     borderColor: 'divider' }}>
                          <Stack direction="row" spacing={1.5}>
                            <Button size="small" variant="outlined"
                                    startIcon={<BsPlusLg />}
                                    onClick={() => {
                                      setPosteDeLaLigne(p.id);
                                      setLigne({ ...LIGNE_VIDE });
                                    }}>
                              Ajouter une ligne
                            </Button>
                            <Button size="small"
                                    onClick={() => ouvrirModeles(p.id)}>
                              Importer un modèle
                            </Button>
                          </Stack>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            Total du chiffrage : {euros(p.total)}
                          </Typography>
                        </Stack>
                      </Paper>
                    </Box>
                  </Collapse>
                </Box>
              ))}
            </Stack>
          )}
        </Fiche>
      </Stack>

      <PosteDialog ouvert={poste !== null} onFermer={() => setPoste(null)}
                   poste={poste} setPoste={setPoste}
                   onEnregistrer={enregistrerPoste} occupe={occupe} />

      <DetailDialog ouvert={ligne !== null} onFermer={() => setLigne(null)}
                    ligne={ligne} setLigne={setLigne}
                    onEnregistrer={enregistrerLigne} occupe={occupe} />

      <Recapitulatif ouvert={recapitulatif}
                     onFermer={() => setRecapitulatif(false)}
                     totaux={totaux} postes={postes} details={lignes} />

      <CorrectTextModal
        show={Boolean(aCorriger)}
        onHide={() => setACorriger(null)}
        correctUrl={aCorriger ? `/postcostings/${aCorriger.id}/correct/` : ''}
        fields={[
          { key: 'title', label: 'Titre', value: aCorriger?.title || '',
            plainText: true, spellingOnly: true },
          { key: 'comment', label: 'Analyse',
            value: aCorriger?.comment || '' },
        ]}
        onSave={async (valeurs) => {
          await axiosInstance.put(`/postcostings/${aCorriger.id}/`,
                                  { ...aCorriger, ...valeurs });
          setACorriger(null);
          setMessage('Correction enregistrée');
          charger();
        }}
      />

      <ThemeProvider theme={themeExpert}>
        <Dialog open={modeles !== null} onClose={() => setModeles(null)}
                fullWidth maxWidth="sm">
          <DialogTitle>Importer un modèle de prix</DialogTitle>
          <DialogContent dividers>
            {modeles?.length === 0 ? (
              <Alert severity="info">
                Aucun modèle de prix pour votre organisation.
              </Alert>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary"
                            sx={{ mb: 2 }}>
                  Ses lignes s'ajoutent à celles du poste ; rien n'est remplacé.
                </Typography>
                <List disablePadding>
                  {(modeles || []).map((modele) => (
                    <ListItemButton key={modele.id} disabled={occupe}
                                    onClick={() => importerModele(modele)}>
                      <ListItemText
                        primary={modele.title}
                        secondary={[
                          modele.description,
                          `${modele.pricingLists?.length ?? 0} ligne(s)`,
                        ].filter(Boolean).join(' — ')}
                      />
                    </ListItemButton>
                  ))}
                </List>
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setModeles(null)}>Fermer</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={posteASupprimer !== null}
                onClose={() => setPosteASupprimer(null)}>
          <DialogTitle>Supprimer ce poste ?</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              « {posteASupprimer?.title} » et <strong>toutes ses lignes de
              chiffrage</strong> seront perdus. C'est définitif.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setPosteASupprimer(null)} disabled={occupe}>
              Annuler
            </Button>
            <Button onClick={supprimerPoste} color="error" variant="contained"
                    disabled={occupe}>
              Supprimer
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={ligneASupprimer !== null}
                onClose={() => setLigneASupprimer(null)}>
          <DialogTitle>Supprimer cette ligne ?</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              « {ligneASupprimer?.title} » sera retirée du chiffrage, et le
              total du poste recalculé.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setLigneASupprimer(null)} disabled={occupe}>
              Annuler
            </Button>
            <Button onClick={supprimerLigne} color="error" variant="contained"
                    disabled={occupe}>
              Supprimer
            </Button>
          </DialogActions>
        </Dialog>
      </ThemeProvider>

      <Snackbar open={Boolean(message)} autoHideDuration={3000}
                onClose={() => setMessage('')} message={message}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </>
  );
}

export default ListCosting;
